import { useState } from 'react';
import { X, Edit3, ChevronLeft, MessageSquare } from 'lucide-react';
import { ScrollArea } from '@/components/mira';
import styles from '@/styles/features/loan-information/chat-model/ChatHistoryModal.module.css';

const ChatHistoryModal = ({
  isChatOpen,
  isVectorClicks,
  setIsChatOpen,
  newChatAction,
  setChatWindowOpens,
  groupedChats,
  setVectorClicks,
  setNewChatOpens,
  setNewChatSelected,
  isHistoryLoading = false,
}) => {
  const [selectedId, setSelectedId] = useState(null);

  if (!isChatOpen && !isVectorClicks) return null;

  const handleClose = () => {
    setIsChatOpen(false);
    setVectorClicks(false);
    setNewChatOpens(false);
  };

  const handleNewChat = () => {
    setNewChatSelected(true);
    newChatAction();
  };

  const handleChatSelect = (chatId) => {
    setVectorClicks(false);
    setIsChatOpen(false);
    setNewChatOpens(false);
    setChatWindowOpens(chatId);
    setSelectedId(chatId);
  };

  return (
    <div className={styles.modalContainer}>
      {/* Header with gradient (preserved from original design) */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {isVectorClicks && (
            <button
              className={styles.backButton}
              onClick={handleClose}
              onFocus={(e) => e.target.blur()}
              tabIndex={-1}
              aria-label="Go back"
            >
              <ChevronLeft className={styles.backIcon} />
            </button>
          )}
          <img
            src="/opus-wordmark.png"
            alt="Opus Capital Markets Consultants"
            className={styles.logo}
            draggable="false"
          />
        </div>

        <button
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close chat history"
        >
          <X className={styles.closeIcon} />
        </button>
      </div>

      {/* New Chat Button */}
      <div className={styles.newChatWrapper}>
        <button
          onClick={handleNewChat}
          className={styles.newChatButton}
          disabled={isHistoryLoading}
          aria-disabled={isHistoryLoading}
          style={isHistoryLoading ? { cursor: 'wait', opacity: 0.5 } : undefined}
        >
          <span>New Chat</span>
          <Edit3 className={styles.newChatIcon} />
        </button>
      </div>

      {/* Chat History List */}
      <ScrollArea className={styles.chatList}>
        {Object.entries(groupedChats)?.map(
          ([timeCategory, chats]) =>
            chats?.length > 0 && (
              <div key={timeCategory} className={styles.section}>
                <h3 className={styles.sectionTitle}>{timeCategory}</h3>

                <div className={styles.chatItems}>
                  {chats?.map((chat) => {
                    const sessionId = chat?.id ?? chat?.chat_id;
                    if (!sessionId) {
                      return null;
                    }
                    const title =
                      chat?.title ||
                      chat?.metadata?.title ||
                      chat?.metadata?.context?.title ||
                      'New Chat';
                    const subtitle =
                      chat?.metadata?.context_summary ||
                      chat?.metadata?.summary_hint ||
                      null;
                    return (
                      <button
                        key={sessionId}
                        className={`${styles.chatItem} ${
                          selectedId === sessionId ? styles.active : ''
                        }`}
                        onClick={() => handleChatSelect(sessionId)}
                        title={title}
                        disabled={isHistoryLoading}
                        aria-disabled={isHistoryLoading}
                        style={isHistoryLoading ? { cursor: 'wait', opacity: 0.5 } : undefined}
                      >
                        <MessageSquare className={styles.chatIcon} />
                        <div className={styles.chatItemContent}>
                          <span className={styles.chatTitle}>{title}</span>
                          {subtitle ? (
                            <span className={styles.chatSubtitle}>{subtitle}</span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )
        )}

        {/* Empty State */}
        {Object.values(groupedChats).every((chats) => chats.length === 0) && (
          <div className={styles.emptyState}>
            <MessageSquare className={styles.emptyStateIcon} />
            <p className={styles.emptyStateText}>No chat history yet</p>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default ChatHistoryModal;
